import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { func, number, object } from 'prop-types'
import { Alert, Checkbox, Table, Tag } from 'antd'
import { downloadCSV } from '../../../../utils'
import { SectorImportModes } from '../../../../utils/api/sector'

import '../../../../assets/sectors-import-report.scss'

export const ImportStatuses = Object.freeze({
  CREATED: 'CREATED',
  UPDATED: 'UPDATED',
  UNCHANGED: 'UNCHANGED',
  ERROR: 'ERROR'
})

const STATUS_COLORS = Object.freeze({
  [ImportStatuses.CREATED]: 'green',
  [ImportStatuses.UPDATED]: 'blue',
  [ImportStatuses.ERROR]: 'red'
})

/** The first four split the rows between them; the last two count some of those rows a second time. */
const COUNTERS = Object.freeze([
  { key: 'created', color: 'green' },
  { key: 'updated', color: 'blue' },
  { key: 'unchanged', color: 'grey' },
  { key: 'errors', color: 'red' },
  { key: 'archived', color: 'gold', subset: true },
  { key: 'unarchived', color: 'purple', subset: true }
])

const PAGE_SIZE = 50
const ROW_HEIGHT = 39
const MIN_BODY_HEIGHT = 120
const CHROME_HEIGHT = 460

const CODE_PATTERN = /^[A-Z][A-Z0-9_]*$/

const EMPTY_VALUE = '\u2014'
const ARROW = '\u2192'

export const translateCode = (code, t) => {
  const [key, ...rest] = String(code).split(':')
  const detail = rest.join(':')

  if (!CODE_PATTERN.test(key)) {
    return t('sectors_import.errors.UNEXPECTED')
  }

  return t(`sectors_import.errors.${key}`, { detail })
}

const printed = value => (value === null || value === undefined || value === '') ? EMPTY_VALUE : value

/** A creation has nothing to compare to, so only an update spells out where the cell comes from. */
const showsOldValue = item => item.status === ImportStatuses.UPDATED

const changesText = item => (item.changes || [])
  .map(change => showsOldValue(item)
    ? `${change.column}: ${printed(change.from)} ${ARROW} ${printed(change.to)}`
    : `${change.column}: ${printed(change.to)}`
  )
  .join(' \u00b7 ')

const reasons = (item, t) => {
  return [item.errors, item.warnings]
    .filter(value => typeof value === 'string' && value !== '')
    .flatMap(value => value.split(', '))
    .filter(code => code !== '')
    .map(code => translateCode(code, t))
    .join(' · ')
}

const SectorsImportReport = ({ height, report, t }) => {
  const isPreview = report.mode === SectorImportModes.DRY_RUN
  const summary = report.summary
  const missing = report.columns.missing

  const [errorsOnly, setErrorsOnly] = useState(summary.errors > 0)

  useEffect(() => setErrorsOnly(summary.errors > 0), [report, summary.errors])

  const rows = useMemo(
    () => errorsOnly ? report.data.filter(item => item.status === ImportStatuses.ERROR) : report.data,
    [errorsOnly, report.data]
  )

  const bodyHeight = useMemo(() => Math.max(
    MIN_BODY_HEIGHT,
    Math.min(Math.min(rows.length, PAGE_SIZE) * ROW_HEIGHT, height - CHROME_HEIGHT)
  ), [height, rows.length])

  const columns = useMemo(() => [
    {
      title: t('sectors_import.report.line'),
      dataIndex: 'line',
      width: 70
    },
    {
      title: t('sectors_import.report.status'),
      dataIndex: 'status',
      width: 130,
      render: status => (
        <Tag color={STATUS_COLORS[status]}>
          {t(`sectors_import.status.${isPreview ? 'preview' : 'done'}.${status}`)}
        </Tag>
      )
    },
    {
      title: t('sectors_import.report.changes'),
      dataIndex: 'changes',
      render: (changes, item) => (
        <ul className='import-changes'>
          {(changes || []).map(change => (
            <li key={change.column}>
              <span className='column'>{t(`sectors_import.columns.${change.column}`)}</span>
              {showsOldValue(item) && (
                <>
                  <span className='from'>{printed(change.from)}</span>
                  <span className='arrow'>{ARROW}</span>
                </>
              )}
              <span className='to'>{printed(change.to)}</span>
            </li>
          ))}
        </ul>
      )
    },
    {
      title: t('sectors_import.report.reasons'),
      key: 'reasons',
      render: (value, item) => reasons(item, t)
    }
  ], [isPreview, t])

  const exportReport = useCallback(() => {
    downloadCSV(report.data.map(item => ({
      [t('sectors_import.report.line')]: item.line,
      [t('sectors_import.report.status')]: t(`sectors_import.status.${isPreview ? 'preview' : 'done'}.${item.status}`),
      [t('sectors_import.report.id')]: item.id,
      [t('sectors_import.report.changes')]: changesText(item),
      [t('sectors_import.report.reasons')]: reasons(item, t),
      ...Object.keys(item.sector).reduce((cells, column) => {
        cells[t(`sectors_import.columns.${column}`)] = item.sector[column]

        return cells
      }, {})
    })))
  }, [isPreview, report.data, t])

  return (
    <div className='sectors-import-report'>
      <Alert
        type={isPreview ? 'info' : (summary.errors > 0 ? 'warning' : 'success')}
        message={t(isPreview ? 'sectors_import.preview.headline' : 'sectors_import.done.headline')}
        showIcon
        style={{ marginBottom: '12px' }}
      />
      {missing.length > 0 && (
        <Alert
          type='warning'
          message={t('sectors_import.missing_columns.title')}
          description={
            <>
              <div>{missing.map(column => t(`sectors_import.columns.${column}`)).join(', ')}</div>
              <div>{t('sectors_import.missing_columns.hint')}</div>
            </>
          }
          showIcon
          style={{ marginBottom: '12px' }}
        />
      )}
      <div className='import-total'>{t('sectors_import.summary.total', { count: summary.total })}</div>
      <div className='import-counters'>
        {COUNTERS.map(counter => (
          <div
            key={counter.key}
            className={`import-counter ${counter.color}${counter.subset ? ' subset' : ''}${summary[counter.key] > 0 ? '' : ' empty'}`}
          >
            <div className='value'>{summary[counter.key]}</div>
            <div className='label'>{t(`sectors_import.summary.${counter.key}`)}</div>
          </div>
        ))}
      </div>
      {summary.errors > 0 && (
        <Checkbox
          checked={errorsOnly}
          onChange={event => setErrorsOnly(event.target.checked)}
          style={{ marginBottom: '8px' }}
        >
          {t('sectors_import.report.errors_only')}
        </Checkbox>
      )}
      <Table
        columns={columns}
        dataSource={rows}
        rowKey='line'
        size='small'
        pagination={{ pageSize: PAGE_SIZE, size: 'small' }}
        scroll={{ y: bodyHeight }}
      />
      <a onClick={exportReport}>{t('sectors_import.report.export')}</a>
    </div>
  )
}

SectorsImportReport.propTypes = {
  height: number.isRequired,
  report: object.isRequired,
  t: func.isRequired
}

export default SectorsImportReport
